import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, Stack, Typography } from "@mui/material";
import { MassDataPanel } from "components/identifyEventsTool/tool/MassDataPanel";
import { extractLabels } from "components/manualReview/utils/formatters";
import { ToggleableAnglesVideoPlayer } from "components/scrapEyeDataDashboard/VideoPlayer/components/ToggleableAnglesVideoPlayer";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useAreaLabelledEvent } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  CommentedEvent,
  useMarkCommentEventReviewedMutation,
} from "src/store/api/generatedApi";
import { CommentEditDrawer } from "./CommentEditDrawer";
import { PLAYBACK_SCOPE } from "components/scrapEyeDataDashboard/VideoPlayer/constants";

dayjs.extend(utc);

const VIDEO_PADDING_SECONDS = 5;
const SEGMENT_SECONDS = 15;
const SCALE_WEIGHT_WINDOW_PADDING_SECONDS = 120;

export const CommentDetailPanel = ({
  commentedEvent,
  location,
}: {
  commentedEvent: CommentedEvent;
  location: string;
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { default_areas: defaultAreas } = useScrapEyeMetadata();
  const withAreaLabels = useAreaLabelledEvent(location);
  const { event } = commentedEvent;
  const [editOpen, setEditOpen] = useState(false);
  const [markReviewed] = useMarkCommentEventReviewedMutation();

  const scrapEyesEvent = useMemo<ScrapEyesEvent>(() => {
    const {
      material,
      coMaterial,
      area,
      comment,
      mass,
      initialMass,
      finalMass,
      confidences,
    } = extractLabels(
      event.labels.map((label) => label.data),
      event.data.event_type,
      defaultAreas
    );
    return withAreaLabels({
      id: event.id.toString(),
      start: dayjs.utc(event.data.start),
      end: dayjs.utc(event.data.end),
      eventType: event.data.event_type,
      material,
      coMaterial,
      area,
      comment,
      mass,
      initialMass,
      finalMass,
      confidences,
    });
  }, [event, defaultAreas, withAreaLabels]);

  const { videoStart, videoEnd, massWindowStart, massWindowEnd } =
    useMemo(() => {
      const snappedStartSeconds =
        Math.floor(
          (scrapEyesEvent.start.unix() - VIDEO_PADDING_SECONDS) /
            SEGMENT_SECONDS
        ) * SEGMENT_SECONDS;
      const snappedEndSeconds =
        Math.ceil(
          (scrapEyesEvent.end.unix() + VIDEO_PADDING_SECONDS) / SEGMENT_SECONDS
        ) * SEGMENT_SECONDS;
      const videoStart = dayjs.unix(snappedStartSeconds).utc();
      const videoEnd = dayjs.unix(snappedEndSeconds).utc();
      return {
        videoStart,
        videoEnd,
        massWindowStart: videoStart.subtract(
          SCALE_WEIGHT_WINDOW_PADDING_SECONDS,
          "second"
        ),
        massWindowEnd: videoEnd.add(
          SCALE_WEIGHT_WINDOW_PADDING_SECONDS,
          "second"
        ),
      };
    }, [scrapEyesEvent]);

  const playerEvents = useMemo(() => [scrapEyesEvent], [scrapEyesEvent]);

  const manualReviewHref = `/${tenantName}/tool/manual-review/${location}?date=${scrapEyesEvent.start.toISOString()}`;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t("comment")}
          </Typography>
          <Typography variant="body1">{commentedEvent.comment}</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0}>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => {
              setEditOpen(true);
            }}
          >
            {t("editEvent")}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CheckIcon />}
            onClick={() => {
              void markReviewed({ tenantName, eventId: event.id });
            }}
          >
            {t("markReviewedNoChange")}
          </Button>
          <Button
            component={Link}
            to={manualReviewHref}
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => {
              void markReviewed({ tenantName, eventId: event.id });
            }}
          >
            {t("openInManualReview")}
          </Button>
        </Stack>
      </Stack>
      <ToggleableAnglesVideoPlayer
        hotkeyScope={PLAYBACK_SCOPE}
        storageKey="commentReview-video-player"
        location={location}
        startIso={videoStart.toISOString()}
        endIso={videoEnd.toISOString()}
        events={playerEvents}
        selectedEventId={scrapEyesEvent.id}
        videoStart={videoStart}
        showVideoControls
        colorEventsBy="material"
      />
      <MassDataPanel
        startIso={massWindowStart.toISOString()}
        endIso={massWindowEnd.toISOString()}
        videoStart={videoStart}
        location={location}
        events={playerEvents}
        candidateEvents={[]}
        includeCandidateEvents
      />
      <CommentEditDrawer
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
        }}
        commentedEvent={commentedEvent}
        event={scrapEyesEvent}
        location={location}
      />
    </Stack>
  );
};
